import {
  forwardRef,
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Alert,
  AudienceIcon,
  Box,
  ClickIcon,
  Column,
  CubeIcon,
  EmptyState,
  FrameStackIcon,
  IconBox,
  type IconBoxProps,
  MergeIcon,
  Row,
  SearchInput,
  SparkleIcon,
  SplitIcon,
  TableIcon,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  TraitIcon,
  VariableIcon,
  TextWithTooltip,
} from "@hightouch/htui";

import boxPlaceholder from "src/assets/placeholders/box.svg";
import { columnReferenceKey } from "src/components/explore/filter-popover/utils";
import { RealtimeBadge } from "src/components/realtime-badge";
import { convertType, NEW_ICON_MAP } from "src/utils/destinations";
import { getSearchRegExp } from "src/utils/string";

import {
  type ColumnModelType,
  type ColumnOption,
  useFormkitContext,
} from "src/formkit/components/formkit-context";
import type { FormProps } from "src/formkit/components/types";
import {
  extractColumnValues,
  formatStaticTraitLabel,
  isAudienceTrait,
} from "src/formkit/components/utils";
import { Link } from "src/router";
import { usePersonalization } from "src/hooks/use-personalization";

const ROW_HEIGHT = 32;

enum ToggleOption {
  Column = "columns",
  Split = "split columns",
  Trait = "traits",
  JourneyContextVariable = "journey context variables",
}

const getGroupHeaderProps = (
  entity: ColumnOption["entity"],
): { icon?: React.ElementType; hidden?: boolean } => {
  if (!entity) return {};

  if (entity.isSplit) {
    return { icon: SplitIcon };
  }

  if (entity.isTrait) {
    return { icon: TraitIcon };
  }

  if (entity.isJourneyContextVariable) {
    return { icon: VariableIcon };
  }

  if (entity.isMerged || entity.modelType === "journey-event") {
    return { icon: MergeIcon };
  }

  return { icon: TableIcon, hidden: true };
};

interface OnChangeValueProp {
  from: string | Record<string, unknown>;
  type: "standard";
  alias?: string;
}

const StandardInput = forwardRef<
  HTMLInputElement,
  Readonly<
    FormProps & {
      onChange: (onClose: () => void, value: OnChangeValueProp) => void;
      onClose: () => void;
    }
  >
>(({ onClose, onChange, value, columnOptions }, ref) => {
  const [search, setSearch] = useState<string>("");
  const [selectedSection, setSelectedSection] = useState<
    ToggleOption | undefined
  >(undefined);
  const { columns: contextColumns, model, variableScope } = useFormkitContext();
  const selectedColumnRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isEventModel = Boolean(model?.isEventModel);
  // Prefer columns passed from the parent Mapper (used for nested array elementOptions).
  const allColumns = columnOptions ?? contextColumns;

  const handleToggleChange = (value: ToggleOption): void => {
    setSelectedSection(value);
    // Scroll the selected section into view
    if (containerRef.current) {
      const container = containerRef.current;
      let indexToScrollTo = 0;

      if (value !== ToggleOption.Column) {
        for (const optionGroup of allColumns) {
          const isSplitGroup = optionGroup.entity?.isSplit;
          const isTraitGroup = optionGroup.entity?.isTrait;
          const isJourneyContextVariableGroup =
            optionGroup.entity?.isJourneyContextVariable;

          if (value === ToggleOption.Split && isSplitGroup) {
            break;
          }

          if (value === ToggleOption.Trait && isTraitGroup) {
            break;
          }

          if (
            value === ToggleOption.JourneyContextVariable &&
            isJourneyContextVariableGroup
          ) {
            break;
          }

          // 1 accounts for the header row
          indexToScrollTo += 1 + (optionGroup.options?.length ?? 0);
        }
      }

      // scrollIntoView does not work well with the Popover as it causes the entire page to scroll
      container.scrollTo({
        behavior: "smooth",
        top: indexToScrollTo * ROW_HEIGHT,
      });
    }
  };

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  const options: ColumnOption[] | undefined = useMemo(() => {
    const nonMbColumns = allColumns?.filter((column) => {
      return column.label !== "boosted";
    });

    if (search) {
      const regex = getSearchRegExp(search, "i");
      const filteredColumns: ColumnOption[] = [];

      for (const column of nonMbColumns ?? []) {
        if (column.options) {
          filteredColumns.push({
            ...column,
            options: column.options.filter(
              (option) =>
                regex.test(option.entity?.name || "") ||
                regex.test(option.label),
            ),
          });
        } else if (
          regex.test(column.entity?.name || "") ||
          regex.test(column.label)
        ) {
          filteredColumns.push(column);
        }
      }
      return filteredColumns;
    }

    return nonMbColumns;
  }, [allColumns, search, value]);

  useEffect(() => {
    if (selectedColumnRef && selectedColumnRef.current) {
      selectedColumnRef.current.scrollIntoView({
        block: "center",
      });
    }
  }, []);

  // Canonical keys for the current selection. Each option carries the same
  // serialization so selection checks are string-equality lookups against
  // these keys. `valueFromKey` matches against the `value.from` shape; the
  // second `valueKey` is for the wider `OnChangeValueProp` comparison used
  // in the bare-options branch below.
  const valueFromKey = useMemo(
    () => columnReferenceKey(value.from),
    [value.from],
  );
  const valueKey = useMemo(() => columnReferenceKey(value), [value]);

  // Precomputed canonical key per option (or grouped sub-option). Rebuilt only
  // when `options` itself changes, so the per-row render path reads the key
  // from a Map instead of re-stringifying.
  const optionKeyByOption = useMemo(() => {
    const map = new Map<ColumnOption, string>();
    for (const option of options ?? []) {
      if (option.options) {
        for (const groupedOption of option.options) {
          map.set(groupedOption, columnReferenceKey(groupedOption.value));
        }
      } else {
        map.set(option, columnReferenceKey(option.value));
      }
    }
    return map;
  }, [options]);

  const hasMultipleGroups = options.length > 1;

  const hasTraitColumns = options.some((option) => option.entity?.isTrait);
  const hasSplitColumns = options.some((option) => option.entity?.isSplit);
  const hasJourneyContextVariableColumns = options.some(
    (option) => option.entity?.isJourneyContextVariable,
  );

  const {
    value: isCantFindColumnDismissed,
    setLocal: setIsCantFindColumnDismissed,
  } = usePersonalization<boolean>({
    key: "syncs.cant-find-column-dismissed",
    initialValue: false,
    useWorkspace: false,
  });

  const cantFindColumnMessage = (
    <>
      We display the columns and traits available for the audience or parent
      model you're syncing, along with any columns that have been{" "}
      <Link
        href={`${import.meta.env.VITE_DOCS_URL}/customer-studio/schema#merge-columns`}
        isExternal
      >
        merged
      </Link>{" "}
      from related models with 1:1 or many:1 relationships. To access additional
      columns, update your{" "}
      {/* FIXME: linking to the schema for the source would be ideal, but I'm not sure how to get the source ID here. sourceId in the formkit context is unset. */}
      <Link href="/schema-v2" isExternal>
        schema
      </Link>{" "}
      or create a{" "}
      <Link href="/traits" isExternal>
        trait
      </Link>{" "}
      to transform your data.
    </>
  );

  // Build a flat Set of all option labels and string values for the search check below
  const existingValues = useMemo(() => extractColumnValues(options), [options]);

  const shouldShowCreateNewFieldOption = useMemo(() => {
    if (!isEventModel) return false;
    const needle = search.trim();
    if (!needle) return false;

    return !existingValues.has(needle);
  }, [isEventModel, existingValues, search]);

  const shouldShowNoResults = useMemo(() => {
    // For event models, "no results" is expected when we haven't ingested any suggestions yet.
    // Only show this state once the user has searched, unless we're not in event-mode.
    if (!isEventModel)
      return options.every((option) => !option?.options?.length);
    if (!search.trim()) return false;
    return options.every((option) => !option?.options?.length);
  }, [isEventModel, options, search]);

  const searchPlaceholder = useMemo(() => {
    if (!isEventModel) return "Search columns…";
    const hasAnyOptions = (options ?? []).some((g) => (g.options ?? []).length);
    return hasAnyOptions ? "Search columns…" : "Enter an expected column…";
  }, [isEventModel, options]);

  const handleEnterPress = (event: React.KeyboardEvent) => {
    if (event.key !== "Enter") return;
    if (!shouldShowCreateNewFieldOption) return;
    const needle = search.trim();
    if (!needle) return;
    onChange(onClose, {
      type: value.type ?? "standard",
      from: needle,
    });
  };

  return (
    <Column flex={1} pl={3} pr={3} pt={1}>
      <Row gap={2}>
        <SearchInput
          autoFocus
          placeholder={searchPlaceholder}
          ref={ref}
          value={search}
          width="100%"
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={handleEnterPress}
        />
        {hasMultipleGroups && (
          <ToggleButtonGroup
            value={selectedSection}
            onChange={(value) => handleToggleChange(value as ToggleOption)}
          >
            <ToggleButton
              aria-label="Columns"
              accessory={{ type: "icon", icon: TableIcon }}
              value={ToggleOption.Column}
              tooltip={{
                message: "Model columns",
                placement:
                  !hasSplitColumns && !hasTraitColumns ? "top-end" : undefined,
              }}
            />
            {hasTraitColumns && (
              <ToggleButton
                aria-label="Trait columns"
                accessory={{ type: "icon", icon: TraitIcon }}
                value={ToggleOption.Trait}
                tooltip={{
                  message: "Trait columns",
                  placement: !hasSplitColumns ? "top-end" : undefined,
                }}
              />
            )}
            {hasSplitColumns && (
              <ToggleButton
                aria-label="Split columns"
                accessory={{ type: "icon", icon: SplitIcon }}
                value={ToggleOption.Split}
                tooltip={{
                  message: "Split columns",
                  placement: !hasJourneyContextVariableColumns
                    ? "top-end"
                    : undefined,
                }}
              />
            )}
            {hasJourneyContextVariableColumns && (
              <ToggleButton
                aria-label="Journey context variables"
                accessory={{ type: "icon", icon: VariableIcon }}
                value={ToggleOption.JourneyContextVariable}
                tooltip={{
                  message: "Journey context variables",
                  placement: "top-end",
                }}
              />
            )}
          </ToggleButtonGroup>
        )}
      </Row>
      <Column
        ref={containerRef}
        position="relative"
        flex="1 0 0px"
        pb={1}
        mt={2}
        overflow="auto"
        tabIndex={0} // Scrollable regions must have keyboard accessibility
        _focus={{ outline: "none" }}
        _focusVisible={{ boxShadow: "outline" }}
      >
        {!isCantFindColumnDismissed &&
          !isEventModel &&
          variableScope !== "realtime-journey" && (
            <Alert
              variant="inline"
              type="subtle"
              title="Can't find a column?"
              onDismiss={() => setIsCantFindColumnDismissed(true)}
              mt={2}
              mb={4}
              message={cantFindColumnMessage}
            />
          )}
        {shouldShowCreateNewFieldOption && (
          <Option
            // Keep key stable for the same search value.
            key={`create-new-field:${search}`}
            showDataSourceColumn={hasMultipleGroups}
            option={{
              label: `Use expected column name "${search.trim()}"…`,
              value: search.trim(),
              type: null,
            }}
            selected={false}
            onClick={() =>
              onChange(onClose, {
                type: value.type ?? "standard",
                from: search.trim(),
              })
            }
          />
        )}
        {options.map((option, index) => {
          if (option.options && option.options.length) {
            const groupLabel = option.label;

            const { icon: groupIcon, hidden: groupHeaderHidden } =
              getGroupHeaderProps(option.entity);

            return (
              <Fragment key={index}>
                {hasMultipleGroups && !groupHeaderHidden && (
                  <Box
                    position="sticky"
                    display="grid"
                    gridTemplateColumns="20px 1fr"
                    columnGap={2}
                    minHeight="40px"
                    px={2}
                    top={0}
                    alignItems="center"
                    bg="base.background"
                    borderBottomWidth="hairline"
                    borderBottomStyle="solid"
                    color="base.border"
                  >
                    <Box
                      as={groupIcon}
                      boxSize={5}
                      color="text.placeholder"
                      mr={3}
                    />
                    <TextWithTooltip
                      color="text.secondary"
                      letterSpacing="wide"
                      size="md"
                    >
                      {groupLabel}
                    </TextWithTooltip>
                  </Box>
                )}

                {option.options.map((groupedOption) => {
                  const optionKey = optionKeyByOption.get(groupedOption) ?? "";
                  const selected = optionKey === valueFromKey;
                  return (
                    <Option
                      key={optionKey}
                      showDataSourceColumn={hasMultipleGroups}
                      ref={selected ? selectedColumnRef : undefined}
                      group={option.label}
                      option={groupedOption}
                      selected={selected}
                      onClick={() =>
                        onChange(onClose, {
                          type: value.type ?? "standard",
                          from: groupedOption.value,
                          alias:
                            groupedOption.suggestedName ??
                            (groupedOption.value !== groupedOption.label
                              ? groupedOption.label
                              : undefined),
                        })
                      }
                    />
                  );
                })}
              </Fragment>
            );
          }
          if (option.options && !option.options.length) {
            return null;
          }
          const optionKey = optionKeyByOption.get(option) ?? "";
          const selected = optionKey === valueKey;
          return (
            <Option
              key={optionKey}
              showDataSourceColumn={hasMultipleGroups}
              ref={selected ? selectedColumnRef : undefined}
              option={option}
              selected={selected}
              onClick={() =>
                onChange(onClose, {
                  type: value.type ?? "standard",
                  from: option.label,
                })
              }
            />
          );
        })}
        {shouldShowNoResults && !shouldShowCreateNewFieldOption && (
          <EmptyState
            variant="minimal"
            imageUrl={boxPlaceholder}
            title="No results"
            mx={8}
            mt={10}
            messageMaxW="100%"
            message={cantFindColumnMessage}
          />
        )}
      </Column>
    </Column>
  );
});

StandardInput.displayName = "StandardInput";
export { StandardInput };

const Option = forwardRef<
  HTMLDivElement,
  {
    group?: string;
    option: ColumnOption;
    selected: boolean;
    onClick: () => void;
    showDataSourceColumn?: boolean;
  }
>(({ group, onClick, option, selected, showDataSourceColumn = false }, ref) => {
  const { customType, description, label, type = "", value } = option;
  const { model } = useFormkitContext();
  const isBoosted = typeof value == "object" && value.type === "boosted";
  const isAudienceTraitOption =
    group === ToggleOption.Trait &&
    typeof value == "object" &&
    typeof value.type === "string" &&
    isAudienceTrait(value.type);
  const Icon = isBoosted
    ? SparkleIcon
    : NEW_ICON_MAP[convertType(customType || type || "")];

  let displayLabel = label;
  if (typeof value === "object" && value.type === "additionalColumnReference") {
    const additionalCol = model?.visual_query_filter?.additionalColumns?.find(
      (c) => c.alias === value.columnAlias,
    );
    if (additionalCol?.column?.type === "static_value") {
      displayLabel = formatStaticTraitLabel(
        label,
        String(additionalCol.column.value),
      );
    }
  }

  return (
    <Box
      ref={ref}
      display="grid"
      gridTemplateColumns="20px 2fr 1fr"
      columnGap={2}
      px={2}
      minHeight="48px"
      _hover={{
        bg: "base.background",
      }}
      alignItems="center"
      bg={selected ? "primary.background" : undefined}
      borderBottomWidth="hairline"
      borderBottomStyle="solid"
      borderColor="base.border"
      cursor="pointer"
      pointerEvents={selected ? "none" : undefined}
      onClick={onClick}
      sx={{
        "&:last-of-type": {
          borderBottom: "none",
        },
      }}
    >
      {Icon ? (
        <Box mr={3}>
          <Box
            as={Icon}
            boxSize={5}
            color={isBoosted ? "warning.400" : "text.placeholder"}
            fontSize="20px"
          />
        </Box>
      ) : (
        <Box boxSize={5} mr={3} />
      )}
      <Row gap={2} whiteSpace="nowrap" minWidth={0}>
        <TextWithTooltip
          color={selected ? "primary.base" : "text.primary"}
          fontWeight="medium"
        >
          {displayLabel}
        </TextWithTooltip>

        {option.isRealtimeTrait && <RealtimeBadge size="sm" />}

        {isAudienceTraitOption && (
          <Tooltip message="Audience trait">
            <Box as={AudienceIcon} boxSize={4} color="text.secondary" />
          </Tooltip>
        )}

        {description && (
          <TextWithTooltip color="text.secondary" size="sm">
            {description}
          </TextWithTooltip>
        )}
      </Row>
      {showDataSourceColumn && (
        <Row gap={2} minWidth={0}>
          {option.entity?.modelType && <ModelIcon entity={option.entity} />}
          <TextWithTooltip color="text.secondary" fontWeight="medium">
            {option.entity?.name ?? "Model"}
          </TextWithTooltip>
        </Row>
      )}
    </Box>
  );
});

Option.displayName = "Option";

const SmallIconBox = ({ icon, color, variant }: IconBoxProps) => {
  return <IconBox size="sm" icon={icon} color={color} variant={variant} />;
};

const ModelIcon = ({
  entity,
}: {
  entity:
    | {
        isSplit: boolean;
        isTrait: boolean;
        isJourneyContextVariable: boolean;
        modelType: ColumnModelType | null;
      }
    | undefined;
}) => {
  if (!entity) return null;

  if (entity.isSplit) {
    return <SmallIconBox icon={<AudienceIcon />} color="blue" />;
  }

  if (entity.isJourneyContextVariable) {
    return <SmallIconBox icon={<VariableIcon />} color="orange" />;
  }

  if (entity.modelType === "model") {
    return <SmallIconBox icon={<CubeIcon />} color="indigo" />;
  }

  if (entity.modelType === "related") {
    return <SmallIconBox icon={<FrameStackIcon />} color="green" />;
  }

  if (entity.modelType === "journey-event") {
    return (
      <SmallIconBox icon={<ClickIcon />} color="teal" variant="inverted" />
    );
  }

  if (entity.isTrait) {
    return <SmallIconBox icon={<TraitIcon />} color="orange" />;
  }

  return null;
};
